<script setup lang="ts">
import logoImg from '@/assets/img/logo.png'
import { ref, computed, onMounted, watch } from 'vue'
import NavButton from '@/components/button/NavButton.vue'
import { PrimeIcons } from '@primevue/core/api'
import { API } from '@/modules/api'
import LanguageSelector from '@/components/LanguageSelector.vue'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/stores'
import { useCartStore } from '@/stores/cart'
import type { UserData } from '@/modules/api/get/userData.ts'
import Checkout from '@/components/checkout/Checkout.vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { navItems } from '@/components/nav/NavData.ts'

const router = useRouter()
const store = useStore()
const cartStore = useCartStore()
const i18n = useI18n()
const sessionStore = useSessionStore()

let userData: UserData | null = null

let showLoggedInElements = ref(false)

const nav = ref([] as any[])

onMounted(async () => {
  // If store is loaded, we can assume user is logged in (since they reached this component)
  if (store.isLoaded) {
    showLoggedInElements.value = true
  } else {
    showLoggedInElements.value = await store.loggedIn()
  }

  if (showLoggedInElements.value) {
    userData = await store.userData()
  }
})

watch(showLoggedInElements, async loggedIn => {
  if (loggedIn) {
    nav.value = await navItems()
  }
})

const logout = async () => {
  await API.Post.logout()
  localStorage.removeItem('auth_token')
  sessionStore.$reset()
  store.$reset()
  cartStore.$reset()
  window.location.href = import.meta.env.VITE_LOBBY_URL
}

const documentLibrary = () => {
  router.push('/documents')
}

const orderHistory = () => {
  router.push('/orderHistory')
}

const invoiceHistory = () => {
  router.push('/invoiceHistory')
}

const autoPaymentSettings = () => {
  router.push('/autoPaymentSettings')
}

const accountSettings = () => {
  router.push('/accountSettings')
}

const aiAssistant = () => {
  router.push('/aiAssistant')
}

const numberOfCartItems = computed(() => {
  let numberOfCartItems = 0
  store.districts.forEach(district => {
    numberOfCartItems += cartStore.getCartLines(district.districtId).length
  })
  return numberOfCartItems
})

// Hide the cart entirely when every one of the family's districts has no way to
// accept online payments (no supported merchant account — DTB-343). Fail open.
const showCart = computed(
  () => store.districts.length === 0 || store.districts.some(d => d.acceptingPayments !== false)
)
</script>

<template>
  <Panel class="MobileNav" toggleable collapsed>
    <template #header>
      <div class="DesktopNav_leftContainer mr-auto">
        <NavButton class="w-full" route="/dashboard">
          <img class="DesktopNav_logo" :src="logoImg" :alt="$t('c.nav.dashboard')" />
        </NavButton>
      </div>
      <Button v-if="showLoggedInElements && showCart" severity="secondary" aria-label="Cart">
        <div v-if="numberOfCartItems > 0">
          <Checkout>
            <OverlayBadge :value="numberOfCartItems" size="small" severity="success">
              <i class="pi pi-shopping-cart" style="font-size: 20px"></i>
            </OverlayBadge>
          </Checkout>
        </div>
        <div v-else>
          <i class="pi pi-shopping-cart" style="font-size: 20px"></i>
        </div>
      </Button>
    </template>
    <template #toggleicon>
      <i :class="'MobileNav_menuIcon ' + PrimeIcons.BARS" aria-label="Menu" />
    </template>
    <Divider class="mt-0" />
    <div class="MobileNav_mobileBtnGroup">
      <NavButton
        v-if="showLoggedInElements"
        v-for="item in nav"
        :name="item.name !== null ? item.name : $t('c.nav.navData.label.' + item.code)"
        :route="item.route"
        :app-id="item.appId"
        :img="item.img"
        :alt="item.name !== null ? item.name : $t('c.nav.navData.label.' + item.code)"
      />
    </div>
    <Divider v-if="showLoggedInElements" />
    <div class="MobileNav_mobileBtnGroup">
      <NavButton
        v-if="userData !== null"
        :name="userData.username"
        :icon="PrimeIcons.USER"
        :alt="$t('c.nav.account')"
      />
      <LanguageSelector />
      <Button v-if="showLoggedInElements" text @click="documentLibrary">{{
        $t('s.accountLink.documents')
      }}</Button>
      <Button v-if="showLoggedInElements" text @click="orderHistory">{{
        $t('s.accountLink.orders')
      }}</Button>
      <Button v-if="showLoggedInElements" text @click="invoiceHistory">{{
        $t('s.accountLink.invoices')
      }}</Button>
      <Button v-if="showLoggedInElements" text @click="accountSettings">
        {{ $t('s.accountLink.accountSettings') }}
      </Button>
      <Button v-if="showLoggedInElements" text @click="aiAssistant">
        {{ $t('s.accountLink.aiAssistant') }}
      </Button>
      <Button v-if="showLoggedInElements" text @click="autoPaymentSettings">
        {{ $t('s.accountLink.autoPaymentSettings') }}
      </Button>
      <Button v-if="showLoggedInElements" text @click="logout">{{
        $t('s.accountLink.logout')
      }}</Button>
    </div>
  </Panel>
</template>

<style scoped>
.DesktopNav_logo {
  width: 100%;
}

.DesktopNav_leftContainer {
  width: 190px;
}

.MobileNav {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  border-radius: 0;
  z-index: 1000;
  max-height: 100vh;
  overflow-y: auto;
}

.MobileNav_menuIcon i {
  font-size: 20px;
}

.MobileNav_mobileBtnGroup {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.MobileNav :deep(.p-panel-content) {
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  padding-bottom: 20px;
}

/* Start and end of menu bar need to match widths for items to center */

@media (min-width: 40rem) {
  /* sm */
}

@media (min-width: 48rem) {
  /* md */
}

@media (min-width: 64rem) {
  /* lg */
}

@media (min-width: 75rem) {
  /* xl */
}

@media (min-width: 80rem) {
  /* 2xl */
}
</style>
